import { useState, useEffect, useCallback, useRef } from 'react';
import { SelectDados } from 'types/DadoComplementar';
import { findAllUsersLotacao, findAllUsersInstituicao } from 'API/fetchUsuarios';
import { saveResponsaveisTurma, findResponsaveisTurma } from 'API/apiAcoes';
import { convertListSelect, extrairIds } from 'utils/Arrays';

export const useTurmaResponsavel = (turmaId: string | null) => {
  const [msgError, setMsgError] = useState<string | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [loadingResp, setLoadingResp] = useState<boolean>(false);
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [tipoRespId, setTipoRespId] = useState<number | null>(null);
  const [institutionId, setInstitutionId] = useState<number | null>(null);
  const [lotacaoId, setLotacaoId] = useState<number | null>(null);
  const [selectedUsersLotacao, setSelectedUsersLotacao] = useState<number[]>([]);
  const [selectedUsersInstituicao, setSelectedUsersInstituicao] = useState<number[]>([]);
  const [usersLotacao, setUsersLotacao] = useState<SelectDados[]>([]);
  const [usersInstituicao, setUsersInstituicao] = useState<SelectDados[]>([]);
  const previousTipoRespId = useRef<number | null>(null);

  useEffect(() => {
    if (msgSuccess) {
      setTimeout(() => {
        setMsgSuccess(null);
      }, 5000);
    }
  }, [msgSuccess]);

  useEffect(() => {
    if (msgError) {
      setTimeout(() => {
        setMsgError(null);
      }, 5000);
    }
  }, [msgError]);

  const carregarResponsaveisTurma = useCallback(async (idTurma: string) => {
    setLoadingResp(true);

    try {
      const response = await findResponsaveisTurma(idTurma);

      if (response) {
        if (response.tipo == 'OFERTA_ESTADUAL') {
          setTipoRespId(1);

          if (response.usuarios) {
            const ids = extrairIds(response.usuarios, 'id');
            setSelectedUsersLotacao(ids);
          }
        }

        if (response.tipo == 'OFERTA_EXTERNA') {
          setTipoRespId(2);

          if (response.instituicao) {
            setInstitutionId(response.instituicao.id);
          }

          if (response.usuarios) {
            const ids = extrairIds(response.usuarios, 'id');
            setSelectedUsersInstituicao(ids);
          }
        }
      }
    } catch (error) {
      setMsgError('Erro ao consultar os responsaveis da turma');
    } finally {
      setLoadingResp(false);
    }
  }, []);

  const loadUsersLotacao = useCallback(async () => {
    if (!tipoRespId || tipoRespId != 1) {
      return;
    }

    setLoadingUsers(true);

    try {
      const result = await findAllUsersLotacao();

      if (!result || result.length === 0) {
        setMsgError('Nenhum usuario encontrado na lotacao');
        setLoadingUsers(false);
        return;
      }

      const usuarios = result.map((item) => {
        const complementos = [
          item.lotacao,
          item.unidade_regional,
          item.diretoria_regional,
          item.superintendencia,
        ].filter((value) => !!value);

        return {
          id: item.id,
          nome: [item.nome, ...complementos].join(' - '),
        };
      });

      setUsersLotacao(usuarios);
    } catch (error) {
      setMsgError('Erro ao buscar usuarios da lotacao');
    } finally {
      setLoadingUsers(false);
    }
  }, [tipoRespId]);

  const loadUsersInstituicao = useCallback(async () => {
    if (!institutionId) return;

    setLoadingUsers(true);

    try {
      const result = await findAllUsersInstituicao(institutionId);

      if (!result || result.length === 0) {
        setMsgError('Nenhum usuario encontrado na instituicao');
        setLoadingUsers(false);
        return;
      }

      const usuarios = convertListSelect(result, 'id', 'nome');
      setUsersInstituicao(usuarios);
    } catch (error) {
      setMsgError('Erro ao buscar usuarios da instituicao');
    } finally {
      setLoadingUsers(false);
    }
  }, [institutionId]);

  const validade = useCallback(() => {
    if (!tipoRespId) {
      setMsgError('Selecione o tipo');
      return false;
    }

    if (tipoRespId == 1 && !selectedUsersLotacao.length) {
      setMsgError('Selecione pelo menos um usuario');
      return false;
    }

    if (tipoRespId == 2 && !institutionId) {
      setMsgError('Selecione a instituicao');
      return false;
    }

    if (tipoRespId == 2 && !selectedUsersInstituicao.length) {
      setMsgError('Selecione pelo menos um usuario');
      return false;
    }

    return true;
  }, [institutionId, selectedUsersInstituicao.length, selectedUsersLotacao.length, tipoRespId]);

  const saveTurmaResp = useCallback(async (): Promise<boolean> => {
    if (!validade()) {
      return false;
    }

    if (!turmaId) {
      setMsgError('Erro ao encontrar turma selecionada');
      return false;
    }

    setLoadingSave(true);

    try {
      const payload = {
        tipo: tipoRespId == 1 ? 'OFERTA_ESTADUAL' : 'OFERTA_EXTERNA',
        instituicao_id: !institutionId ? undefined : institutionId,
        users: tipoRespId == 1 ? selectedUsersLotacao : selectedUsersInstituicao,
      };

      const response = await saveResponsaveisTurma(turmaId, payload);

      if (!response.success) {
        setMsgError('Nao foi possivel cadastrar os responsaveis da turma');
        return false;
      }

      setMsgSuccess('Responsaveis da turma cadastrados com sucesso');
      return true;
    } catch (error) {
      setMsgError('Nao foi possivel cadastrar os responsaveis da turma');
      return false;
    } finally {
      setLoadingSave(false);
    }
  }, [institutionId, selectedUsersInstituicao, selectedUsersLotacao, tipoRespId, turmaId, validade]);

  useEffect(() => {
    if (turmaId) {
      carregarResponsaveisTurma(turmaId);
    }
  }, [carregarResponsaveisTurma, turmaId]);

  useEffect(() => {
    if (institutionId) {
      loadUsersInstituicao();
    }
  }, [institutionId, loadUsersInstituicao]);

  useEffect(() => {
    const changedType =
      previousTipoRespId.current !== null && previousTipoRespId.current !== tipoRespId;

    if (changedType) {
      setInstitutionId(null);
      setLotacaoId(null);
      setSelectedUsersLotacao([]);
      setSelectedUsersInstituicao([]);
    }

    previousTipoRespId.current = tipoRespId;

    if (tipoRespId == 1) {
      loadUsersLotacao();
    }
  }, [loadUsersLotacao, tipoRespId]);

  const handleUsersAreaChange = (value: (string | number)[]) => {
    const numericIds = value.map(id => Number(id));
    setSelectedUsersLotacao(numericIds);
  };

  const handleUsersInstChange = (value: (string | number)[]) => {
    const numericIds = value.map(id => Number(id));
    setSelectedUsersInstituicao(numericIds);
  };

  return {
    msgError,
    msgSuccess,
    loadingResp,
    loadingSave,
    loadingUsers,
    tipoRespId,
    setTipoRespId,
    institutionId,
    setInstitutionId,
    lotacaoId,
    setLotacaoId,
    selectedUsersLotacao,
    setSelectedUsersLotacao,
    selectedUsersInstituicao,
    setSelectedUsersInstituicao,
    usersLotacao,
    usersInstituicao,
    loadUsersLotacao,
    loadUsersInstituicao,
    handleUsersAreaChange,
    handleUsersInstChange,
    saveTurmaResp,
  };
};
